import { CampaignSectionState } from 'sections/onsite-campaigns/context/OnsiteCampaignSectionState';
import { CampaignEditorState } from '../../../contexts/CampaignEditorState';
import { TriggerSegmentationRules, OnsiteMessageSegment } from 'sections/onsite-campaigns/types';
import {
  OnsiteCampaignResponse,
  OnsiteCampaignStatus,
  ExitIntentAreas,
  AllowList,
  OnClickConfig,
} from 'sections/onsite-campaigns/types';
import { OnsiteMessageDisplayType } from 'sections/onsite-campaigns/types/enums/OnsiteMessageDisplayType';
import { convertSegmentRuleValues } from '../utils/convertSegmentRuleValues';
import { MessageSegmentationMapper } from './MessageSegmentationMapper';
import { CanvasState } from '../../../tabs/CampaignWorkflowTab/components/Canvas/state/CanvasState';
import { setTimeFor } from '../utils/datetime';

const mapTriggerSegmentation = (state: CampaignEditorState): TriggerSegmentationRules => {
  if (state.trigger.segmentation?.length && state.trigger.segmentation[0].length) {
    return convertSegmentRuleValues(state.trigger.segmentation, true);
  }
};

export class CampaignStateToResponseAdapter implements OnsiteCampaignResponse {
  public constructor(
    sectionState: CampaignSectionState,
    prevEditorState: CampaignEditorState,
    editorState: CampaignEditorState,
    canvasState: CanvasState,
    flags?: {
      isLiveTrendsEnabled: boolean;
    },
  ) {
    this.id = sectionState.selectedCampaign.id;
    this.name = editorState.name;
    this.status = editorState.status;
    this.allowList = editorState?.allowList ?? [];
    this.emailAllowList = editorState?.emailAllowList;
    if (flags?.isLiveTrendsEnabled) {
      this.lookBackPeriodInMinutes = editorState?.lookBackPeriodInMinutes;
    }
    this.updatedBy = sectionState.selectedCampaign.updatedBy;
    /** Deprecated */
    this.displayType = OnsiteMessageDisplayType.Overlay;
    this.frequency = editorState.trigger.frequency;

    // onLoad is standalone and overrides all other triggers
    if (editorState.trigger?.onPageLoad) {
      this.triggers = {
        onLoad: true,
      };
    } else {
      const triggers = {
        ...(editorState.trigger?.exitIntentAreas &&
          editorState.trigger.exitIntentAreas.length > 0 && {
            exitFrame: editorState.trigger.exitIntentAreas,
          }),
        ...(editorState.trigger?.inactivity && { inactivity: editorState.trigger.inactivity * 1000 }),
        ...(editorState.trigger?.onClick?.selector && {
          onClick: editorState.trigger.onClick,
        }),
      };

      if (Object.keys(triggers).length > 0) {
        this.triggers = triggers;
      }
    }
    this.triggerSegmentationRules = mapTriggerSegmentation(editorState);
    this.showSegmentationSplit = !!Object.keys(editorState.branches).length;
    this.onsiteMessageSegments = MessageSegmentationMapper.Map(
      prevEditorState,
      editorState,
      canvasState,
    ) as OnsiteMessageSegment[];

    const startDate = editorState.scheduling.startDate;
    if (startDate) {
      const hoursAndMinutes = editorState.scheduling.startTime.split(':');
      setTimeFor(startDate, { hours: Number(hoursAndMinutes[0] ?? 0), minutes: Number(hoursAndMinutes[1] ?? 0) });
      this.startDate = startDate.getTime();
    }

    const endDate = editorState.scheduling?.endDate;
    if (endDate && editorState.scheduling?.endTime) {
      const hoursAndMinutes = editorState.scheduling?.endTime.split(':');
      setTimeFor(endDate, { hours: Number(hoursAndMinutes[0] ?? 0), minutes: Number(hoursAndMinutes[1] ?? 0) });
      this.endDate = endDate.getTime();
    }
  }

  public id: string;
  public name: string;
  public status: OnsiteCampaignStatus;
  public templateId: string;
  public triggers?: { onLoad?: boolean; exitFrame?: ExitIntentAreas[]; inactivity?: number; onClick?: OnClickConfig };
  public displayType: OnsiteMessageDisplayType;
  public updatedBy: string;
  public allowList: AllowList[];
  public emailAllowList: string[];
  public lookBackPeriodInMinutes: number;
  public frequency: string;
  public offsiteMessage: { subjectLineId: string; messageTemplateId: string };
  public triggerSegmentationRules?: TriggerSegmentationRules;
  public showSegmentationSplit: boolean;
  public onsiteMessageSegments?: any;
  // Scheduling
  public startDate?: number;
  public endDate?: number;
}
